import { useCallback, useEffect, useRef, useState } from "react";
import {
  Mic,
  MicOff,
  Video,
  VideoOff,
  PhoneOff,
  MonitorUp,
  MonitorX,
  Maximize,
  Minimize,
  Camera,
  AlertTriangle,
} from "lucide-react";
import {
  ensureSignalingRoom,
  publishAnswer,
  publishIceCandidate,
  publishOffer,
  subscribeToIceCandidates,
  subscribeToSignalingDoc,
} from "../../services/webrtcSignalingService";
import { getTurnCredentials } from "../../utils/getTurnCredentials";
import { normalizeRole, ROLE_VALUE } from "../../constants/roleLabels";
import { logger } from "../../utils/logger";

function formatDuration(totalSeconds) {
  const safe = Math.max(0, totalSeconds || 0);
  const minutes = Math.floor(safe / 60)
    .toString()
    .padStart(2, "0");
  const seconds = (safe % 60).toString().padStart(2, "0");
  return `${minutes}:${seconds}`;
}

function getInitial(name) {
  const trimmed = (name || "").trim();
  return trimmed ? trimmed.charAt(0).toUpperCase() : "?";
}

function mapConnectionStatus(pcState) {
  const state = String(pcState || "").toLowerCase();
  if (state === "connected") return "connected";
  if (state === "connecting" || state === "new") return "connecting";
  if (state === "failed" || state === "disconnected" || state === "closed") {
    return "failed";
  }
  return "connecting";
}

function formatIceServerTypes(types) {
  if (!types) return "none";
  const parts = [];
  if (types.stun) parts.push(`STUN=${types.stun}`);
  if (types.turn) parts.push(`TURN=${types.turn}`);
  if (types.turns) parts.push(`TURNS=${types.turns}`);
  if (types.other) parts.push(`other=${types.other}`);
  return parts.length ? parts.join(", ") : "none";
}

/** Structured console diagnostics for Metered TURN / ICE debugging. */
function logWebRtcDiagnostics({
  turnStatus,
  reason,
  iceServerCount,
  iceServerTypes,
  iceConnectionState,
  connectionState,
}) {
  logger.log(
    [
      "SkillSyncs WebRTC Diagnostics:",
      `  TURN status: ${turnStatus}`,
      reason ? `  Reason: ${reason}` : null,
      `  ICE servers loaded: ${iceServerCount ?? 0}`,
      `  ICE server types: ${formatIceServerTypes(iceServerTypes)}`,
      `  ICE connection state: ${iceConnectionState || "n/a"}`,
      `  Connection state: ${connectionState || "n/a"}`,
    ]
      .filter(Boolean)
      .join("\n")
  );
}

function WebRTCCall({
  problemId,
  currentUser,
  currentUserRole,
  isCaller,
  remoteScreenSharing = false,
  localName = "You",
  remoteName,
  onCallStarted,
  onCallEnded,
  onLoadError,
  onMediaStateChange,
  onConnectionStateChange,
  onCaptureFrame,
}) {
  const localVideoRef = useRef(null);
  const remoteVideoRef = useRef(null);
  const pcRef = useRef(null);
  const pcPromiseRef = useRef(null);
  // Cached Metered/STUN ICE config for this call attempt (set before PC create).
  const iceConfigRef = useRef(null);
  const localStreamRef = useRef(null);
  const remoteStreamRef = useRef(null);
  const signalingUnsubRef = useRef(null);
  const iceUnsubRef = useRef(null);
  const processedIceIds = useRef(new Set());
  const screenStreamRef = useRef(null);
  const containerRef = useRef(null);
  // Holds a screen-share capture that is waiting for the user to confirm in the
  // "sharing this tab" warning overlay before we wire it into the call.
  const pendingScreenRef = useRef(null);

  const [error, setError] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [connectionState, setConnectionState] = useState("new");
  const [iceConnectionState, setIceConnectionState] = useState("new");
  // Diagnostic states for TURN / peer connection health (exposed for debugging).
  const [turnStatus, setTurnStatus] = useState("loading"); // loading | available | unavailable
  const [connectionStatus, setConnectionStatus] = useState("idle"); // idle | connecting | connected | failed
  const [turnReason, setTurnReason] = useState(null);
  const [iceDiagnostics, setIceDiagnostics] = useState({
    iceServerCount: 0,
    iceServerTypes: { stun: 0, turn: 0, turns: 0, other: 0 },
  });
  // Bumping this re-runs the media/signaling init effect (Retry connection).
  const [callAttempt, setCallAttempt] = useState(0);
  const [localScreenSharing, setLocalScreenSharing] = useState(false);
  const [audioEnabled, setAudioEnabled] = useState(true);
  const [videoEnabled, setVideoEnabled] = useState(true);
  const [hasEnded, setHasEnded] = useState(false);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  // Local camera stream kept in state so a dedicated effect can (re)attach it to
  // the <video> element — the imperative assignment alone is fragile (see below).
  const [localStream, setLocalStream] = useState(null);
  const [screenShareWarning, setScreenShareWarning] = useState(false);
  const [capturing, setCapturing] = useState(false);
  const uid = currentUser?.uid;

  const iceConnected = ["connected", "completed"].includes(iceConnectionState);

  const applyIceConfigDiagnostics = useCallback((config, extras = {}) => {
    setTurnStatus(config.turnStatus);
    setTurnReason(config.reason || null);
    setIceDiagnostics({
      iceServerCount: config.iceServerCount,
      iceServerTypes: config.iceServerTypes,
    });
    logWebRtcDiagnostics({
      turnStatus: config.turnStatus,
      reason: config.reason,
      iceServerCount: config.iceServerCount,
      iceServerTypes: config.iceServerTypes,
      iceConnectionState: extras.iceConnectionState || "n/a",
      connectionState: extras.connectionState || "n/a",
    });
  }, []);

  const setupPeerConnection = useCallback(async () => {
    if (pcRef.current) return pcRef.current;
    // Dedupe concurrent callers (e.g. signaling doc + ICE candidates arriving at
    // the same time) so we only ever fetch credentials and build one connection.
    if (pcPromiseRef.current) return pcPromiseRef.current;

    pcPromiseRef.current = (async () => {
      // Prefer ICE config loaded during init. If missing (late ICE path), fetch now.
      // TURN is required for users behind strict NAT/firewalls.
      // Falling back silently to STUN-only causes unpredictable failures.
      let config = iceConfigRef.current;
      if (!config) {
        config = await getTurnCredentials();
        iceConfigRef.current = config;
        applyIceConfigDiagnostics(config);
      }

      if (config.turnStatus === "unavailable") {
        const err = new Error(
          config.reason || "TURN credentials unavailable"
        );
        err.code = "TURN_UNAVAILABLE";
        throw err;
      }

      const pc = new RTCPeerConnection({ iceServers: config.iceServers });
      pcRef.current = pc;

      pc.onicecandidate = async (event) => {
        if (!event.candidate || !uid) return;
        try {
          await publishIceCandidate(problemId, uid, event.candidate);
          logger.log("[CALL] ICE candidate published");
        } catch (err) {
          console.warn("[CALL] ICE candidate publish failed", err);
        }
      };

      pc.ontrack = (event) => {
        if (!remoteStreamRef.current) {
          remoteStreamRef.current = new MediaStream();
        }
        event.streams[0].getTracks().forEach((track) => {
          remoteStreamRef.current.addTrack(track);
        });
        if (remoteVideoRef.current) {
          remoteVideoRef.current.srcObject = remoteStreamRef.current;
        }
      };

      pc.onconnectionstatechange = () => {
        const state = pc.connectionState;
        setConnectionState(state);
        setConnectionStatus(mapConnectionStatus(state));
        onConnectionStateChange?.(state);
        logger.log(`[CALL] connection state: ${state}`);
        logWebRtcDiagnostics({
          turnStatus: iceConfigRef.current?.turnStatus || "n/a",
          reason: iceConfigRef.current?.reason || null,
          iceServerCount: iceConfigRef.current?.iceServerCount,
          iceServerTypes: iceConfigRef.current?.iceServerTypes,
          iceConnectionState: pc.iceConnectionState,
          connectionState: state,
        });
        if (state === "connected") {
          onCallStarted?.();
        }
        if (["disconnected", "failed", "closed"].includes(state)) {
          onCallEnded?.();
        }
      };

      pc.oniceconnectionstatechange = () => {
        const iceState = pc.iceConnectionState;
        setIceConnectionState(iceState);
        logger.log("ICE connection state:", iceState);
        if (iceState === "failed") {
          setConnectionStatus("failed");
          console.error(
            "ICE connection failed — check TURN status; relay may be required"
          );
          logWebRtcDiagnostics({
            turnStatus: iceConfigRef.current?.turnStatus || "n/a",
            reason: iceConfigRef.current?.reason || null,
            iceServerCount: iceConfigRef.current?.iceServerCount,
            iceServerTypes: iceConfigRef.current?.iceServerTypes,
            iceConnectionState: iceState,
            connectionState: pc.connectionState,
          });
        }
        if (iceState === "connected" || iceState === "completed") {
          logger.log("WebRTC connection established successfully");
        }
      };

      return pc;
    })();

    return pcPromiseRef.current;
  }, [
    applyIceConfigDiagnostics,
    onCallEnded,
    onCallStarted,
    onConnectionStateChange,
    problemId,
    uid,
  ]);

  const startLocalMedia = useCallback(async () => {
    const stream = await navigator.mediaDevices.getUserMedia({
      video: true,
      audio: true,
    });
    localStreamRef.current = stream;
    // Drive attachment through state so the effect below re-attaches on any
    // re-render/remount (fixes the black "You" panel).
    setLocalStream(stream);
    if (localVideoRef.current) {
      localVideoRef.current.srcObject = stream;
    }

    const pc = await setupPeerConnection();
    stream.getTracks().forEach((track) => pc.addTrack(track, stream));

    onMediaStateChange?.({
      joined: true,
      video: true,
      audio: true,
      screenSharing: false,
    });
  }, [onMediaStateChange, setupPeerConnection]);

  const initCallerFlow = useCallback(async () => {
    if (!uid) return;
    const pc = await setupPeerConnection();
    await ensureSignalingRoom(problemId, uid);

    const offer = await pc.createOffer();
    await pc.setLocalDescription(offer);
    await publishOffer(problemId, uid, offer);
  }, [problemId, setupPeerConnection, uid]);

  const applyOfferAndAnswer = useCallback(async (offerPayload) => {
    const pc = await setupPeerConnection();
    if (!uid) return;
    if (!offerPayload?.sdp || offerPayload?.senderUid === uid) return;
    if (pc.currentRemoteDescription) return;
    await pc.setRemoteDescription(
      new RTCSessionDescription({
        type: offerPayload.type || "offer",
        sdp: offerPayload.sdp,
      })
    );
    const answer = await pc.createAnswer();
    await pc.setLocalDescription(answer);
    await publishAnswer(problemId, uid, answer);
  }, [problemId, setupPeerConnection, uid]);

  const applyRemoteAnswer = useCallback(async (answerPayload) => {
    const pc = await setupPeerConnection();
    if (!uid) return;
    if (!answerPayload?.sdp || answerPayload?.senderUid === uid) return;
    if (pc.currentRemoteDescription) return;
    await pc.setRemoteDescription(
      new RTCSessionDescription({
        type: answerPayload.type || "answer",
        sdp: answerPayload.sdp,
      })
    );
    logger.log("[CALL] remote answer applied");
  }, [setupPeerConnection, uid]);

  useEffect(() => {
    if (!problemId || !uid) return;
    let isMounted = true;
    const processedCandidateIds = processedIceIds.current;

    const init = async () => {
      try {
        setError(null);
        setHasEnded(false);
        setTurnStatus("loading");
        setConnectionStatus("idle");
        setTurnReason(null);
        iceConfigRef.current = null;
        pcPromiseRef.current = null;

        // Resolve TURN before media/signaling so we never silently enter a
        // "Connecting..." session on STUN-only after a Metered failure.
        const config = await getTurnCredentials();
        if (!isMounted) return;
        iceConfigRef.current = config;
        applyIceConfigDiagnostics(config);

        if (config.turnStatus === "unavailable") {
          // TURN is required for users behind strict NAT/firewalls.
          // Falling back silently to STUN-only causes unpredictable failures.
          setTurnStatus("unavailable");
          setConnectionStatus("idle");
          return;
        }

        setTurnStatus("available");
        setConnectionStatus("connecting");

        await startLocalMedia();
        if (!isMounted) return;

        await ensureSignalingRoom(problemId, isCaller ? uid : null);
        if (!isMounted) return;

        signalingUnsubRef.current = subscribeToSignalingDoc(problemId, async (doc) => {
          if (!doc || !isMounted) return;
          try {
            if (isCaller) {
              await applyRemoteAnswer(doc.answer);
            } else {
              await applyOfferAndAnswer(doc.offer);
            }
          } catch (err) {
            console.warn("[CALL] signaling apply error", err);
          }
        });

        iceUnsubRef.current = subscribeToIceCandidates(problemId, async (candidateDoc) => {
          if (!isMounted || !candidateDoc || candidateDoc.senderUid === uid) return;
          if (processedCandidateIds.has(candidateDoc.id)) return;
          processedCandidateIds.add(candidateDoc.id);
          try {
            const pc = await setupPeerConnection();
            await pc.addIceCandidate(
              new RTCIceCandidate({
                candidate: candidateDoc.candidate,
                sdpMid: candidateDoc.sdpMid,
                sdpMLineIndex: candidateDoc.sdpMLineIndex,
              })
            );
            logger.log("[CALL] ICE candidate received");
          } catch (err) {
            if (err?.code === "TURN_UNAVAILABLE") {
              setTurnStatus("unavailable");
              setTurnReason(err.message);
              return;
            }
            console.warn("[CALL] addIceCandidate failed", err);
          }
        });

        if (isCaller) {
          await initCallerFlow();
        }
      } catch (err) {
        console.error("[CALL] init failed", err);
        if (!isMounted) return;
        if (err?.code === "TURN_UNAVAILABLE") {
          setTurnStatus("unavailable");
          setTurnReason(err.message);
          setConnectionStatus("idle");
          return;
        }
        setError("Could not initialize call media/signaling.");
        setConnectionStatus("failed");
        onLoadError?.(err);
      }
    };

    init();

    return () => {
      isMounted = false;
      onMediaStateChange?.({
        joined: false,
        video: false,
        audio: false,
        screenSharing: false,
      });

      if (signalingUnsubRef.current) {
        signalingUnsubRef.current();
        signalingUnsubRef.current = null;
      }
      if (iceUnsubRef.current) {
        iceUnsubRef.current();
        iceUnsubRef.current = null;
      }
      if (pcRef.current) {
        pcRef.current.close();
        pcRef.current = null;
      }
      pcPromiseRef.current = null;
      iceConfigRef.current = null;
      if (localStreamRef.current) {
        localStreamRef.current.getTracks().forEach((t) => t.stop());
        localStreamRef.current = null;
      }
      if (remoteStreamRef.current) {
        remoteStreamRef.current.getTracks().forEach((t) => t.stop());
        remoteStreamRef.current = null;
      }
      setLocalStream(null);
      processedCandidateIds.clear();
    };
  }, [
    applyIceConfigDiagnostics,
    applyOfferAndAnswer,
    applyRemoteAnswer,
    callAttempt,
    initCallerFlow,
    isCaller,
    onLoadError,
    onMediaStateChange,
    problemId,
    setupPeerConnection,
    startLocalMedia,
    uid,
  ]);

  // ISSUE 1 fix — keep the local camera feed attached to the "You" panel.
  // Setting srcObject only imperatively inside startLocalMedia is fragile: if the
  // <video> element remounts (layout changes) or the assignment races with mount,
  // the panel goes black and nothing re-attaches it. This effect re-attaches the
  // stream whenever it (or the screen-share state) changes, and explicitly calls
  // play() so a stalled muted-autoplay can't leave the panel black. When screen
  // sharing, the screen-share code owns srcObject, so we skip re-attaching here.
  useEffect(() => {
    const el = localVideoRef.current;
    if (!el || localScreenSharing) return;
    if (localStream && el.srcObject !== localStream) {
      el.srcObject = localStream;
    }
    if (el.srcObject) {
      const playResult = el.play?.();
      if (playResult && typeof playResult.catch === "function") {
        playResult.catch(() => {});
      }
    }
  }, [localStream, localScreenSharing]);

  // Session duration timer: counts up from 00:00 once the media connection is
  // established, resets whenever the connection drops.
  useEffect(() => {
    if (!iceConnected) {
      setElapsedSeconds(0);
      return undefined;
    }
    const intervalId = setInterval(() => {
      setElapsedSeconds((prev) => prev + 1);
    }, 1000);
    return () => clearInterval(intervalId);
  }, [iceConnected]);

  // Keep local fullscreen state in sync with the browser (handles the user
  // pressing Esc to leave fullscreen, which does not go through our button).
  useEffect(() => {
    const handleChange = () => {
      const fsEl =
        document.fullscreenElement || document.webkitFullscreenElement;
      setIsFullscreen(Boolean(fsEl));
    };
    document.addEventListener("fullscreenchange", handleChange);
    document.addEventListener("webkitfullscreenchange", handleChange);
    return () => {
      document.removeEventListener("fullscreenchange", handleChange);
      document.removeEventListener("webkitfullscreenchange", handleChange);
    };
  }, []);

  const toggleFullscreen = () => {
    const el = containerRef.current;
    if (!el) return;
    const fsEl = document.fullscreenElement || document.webkitFullscreenElement;
    if (!fsEl) {
      const request =
        el.requestFullscreen ||
        el.webkitRequestFullscreen ||
        el.msRequestFullscreen;
      request?.call(el);
    } else {
      const exit =
        document.exitFullscreen ||
        document.webkitExitFullscreen ||
        document.msExitFullscreen;
      exit?.call(document);
    }
  };

  const toggleAudio = () => {
    const stream = localStreamRef.current;
    if (!stream) return;
    const track = stream.getAudioTracks()[0];
    if (!track) return;
    track.enabled = !track.enabled;
    setAudioEnabled(track.enabled);
    onMediaStateChange?.({ audio: track.enabled });
  };

  const toggleVideo = () => {
    const stream = localStreamRef.current;
    if (!stream) return;
    const track = stream.getVideoTracks()[0];
    if (!track) return;
    track.enabled = !track.enabled;
    setVideoEnabled(track.enabled);
    onMediaStateChange?.({ video: track.enabled });
  };

  // End Call: stop local media, close the peer connection, then notify the
  // parent (WorkspacePage) via onCallEnded — which updates the calls/{roomId}
  // document status to "ended" through endCallPresence.
  const handleEndCall = useCallback(() => {
    try {
      if (localStreamRef.current) {
        localStreamRef.current.getTracks().forEach((t) => t.stop());
        localStreamRef.current = null;
      }
      if (pcRef.current) {
        pcRef.current.close();
        pcRef.current = null;
      }
      pcPromiseRef.current = null;
      iceConfigRef.current = null;
    } catch (err) {
      console.warn("[CALL] end call cleanup error", err);
    }
    setLocalStream(null);
    setHasEnded(true);
    setConnectionStatus("failed");
    onCallEnded?.();
  }, [onCallEnded]);

  // Re-fetch Metered TURN credentials and re-init media/signaling without
  // leaving the workspace session.
  const handleRetryConnection = useCallback(() => {
    logger.log("[CALL] Retrying WebRTC connection / TURN credentials...");
    try {
      if (signalingUnsubRef.current) {
        signalingUnsubRef.current();
        signalingUnsubRef.current = null;
      }
      if (iceUnsubRef.current) {
        iceUnsubRef.current();
        iceUnsubRef.current = null;
      }
      if (pcRef.current) {
        pcRef.current.close();
        pcRef.current = null;
      }
      pcPromiseRef.current = null;
      iceConfigRef.current = null;
      if (localStreamRef.current) {
        localStreamRef.current.getTracks().forEach((t) => t.stop());
        localStreamRef.current = null;
      }
      setLocalStream(null);
    } catch (err) {
      console.warn("[CALL] retry cleanup error", err);
    }
    setError(null);
    setHasEnded(false);
    setTurnStatus("loading");
    setConnectionStatus("idle");
    setTurnReason(null);
    setConnectionState("new");
    setIceConnectionState("new");
    processedIceIds.current.clear();
    setCallAttempt((n) => n + 1);
  }, []);

  // Wires an approved screen capture into the call: swaps the outgoing video
  // track, previews it locally, and restores the camera when sharing stops.
  const finalizeScreenShare = useCallback(
    async (screenStream, screenTrack, sender) => {
      screenStreamRef.current = screenStream;
      if (sender) {
        await sender.replaceTrack(screenTrack);
      }
      if (localVideoRef.current) {
        localVideoRef.current.srcObject = screenStream;
      }
      onMediaStateChange?.({ screenSharing: true, video: true });
      setLocalScreenSharing(true);

      screenTrack.onended = async () => {
        const cameraTrack = localStreamRef.current
          ?.getVideoTracks()
          ?.find(Boolean);
        if (sender && cameraTrack) {
          await sender.replaceTrack(cameraTrack);
        }
        if (localVideoRef.current && localStreamRef.current) {
          localVideoRef.current.srcObject = localStreamRef.current;
        }
        screenStreamRef.current = null;
        onMediaStateChange?.({ screenSharing: false });
        setLocalScreenSharing(false);
      };
    },
    [onMediaStateChange]
  );

  const startScreenShare = async () => {
    const pc = pcRef.current;
    if (!pc) return;
    try {
      const screenStream = await navigator.mediaDevices.getDisplayMedia({
        video: true,
      });
      const screenTrack = screenStream.getVideoTracks()[0];
      const sender = pc
        .getSenders()
        .find((s) => s.track && s.track.kind === "video");

      // ISSUE 2 — detect the recursive "screen-within-screen" case where the user
      // picks this very browser tab as the share source. Tab captures are labelled
      // with the tab's title/host, so match against our brand, the current host,
      // localhost, and the document title.
      const trackLabel = (screenTrack?.label || "").toLowerCase();
      const windowTitle = (document.title || "").toLowerCase();
      const host = (window.location.host || "").toLowerCase();
      const sharingThisTab =
        trackLabel.includes("skillsyncs") ||
        trackLabel.includes("localhost") ||
        (host && trackLabel.includes(host)) ||
        (windowTitle && trackLabel.includes(windowTitle));

      if (sharingThisTab) {
        // Hold the capture and require an explicit choice before wiring it in.
        pendingScreenRef.current = { stream: screenStream, track: screenTrack, sender };
        setScreenShareWarning(true);
        return;
      }

      await finalizeScreenShare(screenStream, screenTrack, sender);
    } catch (err) {
      console.warn("[CALL] screen share denied/failed", err);
    }
  };

  // User confirmed "Continue anyway" on the mirror-loop warning.
  const handleContinueScreenShare = async () => {
    const pending = pendingScreenRef.current;
    pendingScreenRef.current = null;
    setScreenShareWarning(false);
    if (pending) {
      await finalizeScreenShare(pending.stream, pending.track, pending.sender);
    }
  };

  // User chose "Stop Sharing" on the warning — tear the capture down immediately
  // so it is never sent into the call (prevents the mirror loop entirely).
  const handleStopWarningShare = () => {
    const pending = pendingScreenRef.current;
    pendingScreenRef.current = null;
    setScreenShareWarning(false);
    if (pending?.stream) {
      pending.stream.getTracks().forEach((t) => t.stop());
    }
  };

  // Stop sharing programmatically (from the banner button). Stopping the track
  // fires its onended handler above, which restores the camera feed.
  const stopScreenShare = () => {
    const track = screenStreamRef.current?.getVideoTracks?.()[0];
    if (track) {
      track.stop();
      if (typeof track.onended === "function") {
        track.onended();
      }
    }
  };

  /**
   * Grabs the current frame from the primary video — the shared screen when
   * one is live, otherwise the remote camera — and hands it to the workspace
   * as evidence. That is how an error on screen becomes a file the findings
   * can cite, instead of a sentence in chat that nobody can replay.
   */
  const handleCaptureFrame = async () => {
    if (!onCaptureFrame || capturing) return;

    const preferLocalScreen = localScreenSharing;
    const preferRemoteScreen = remoteScreenSharing && !localScreenSharing;
    const sourceEl = preferLocalScreen
      ? localVideoRef.current
      : preferRemoteScreen
      ? remoteVideoRef.current
      : remoteVideoRef.current?.readyState >= 2
      ? remoteVideoRef.current
      : localVideoRef.current;

    if (!sourceEl || sourceEl.readyState < 2 || !sourceEl.videoWidth) {
      return;
    }

    const canvas = document.createElement("canvas");
    canvas.width = sourceEl.videoWidth;
    canvas.height = sourceEl.videoHeight;
    canvas.getContext("2d").drawImage(sourceEl, 0, 0);

    setCapturing(true);
    try {
      const blob = await new Promise((resolve, reject) => {
        canvas.toBlob(
          (result) =>
            result
              ? resolve(result)
              : reject(new Error("The frame could not be captured.")),
          "image/png"
        );
      });
      const stamp = new Date().toISOString().replace(/[:.]/g, "-");
      const file = new File([blob], `session-frame-${stamp}.png`, {
        type: "image/png",
      });
      await onCaptureFrame(file);
    } catch (error) {
      console.error("Could not capture session frame:", error);
    } finally {
      setCapturing(false);
    }
  };

  if (error) {
    return (
      <div className="w-full flex items-center justify-center text-center text-sm text-slate-700 bg-white border border-slate-200 rounded-xl p-6">
        {error}
      </div>
    );
  }

  if (turnStatus === "unavailable") {
    return (
      <div
        className="w-full bg-white border border-amber-200 rounded-xl p-6 text-center"
        data-turn-status={turnStatus}
        data-connection-status={connectionStatus}
        data-ice-server-count={iceDiagnostics.iceServerCount}
      >
        <p className="text-sm text-slate-700 leading-relaxed mb-5">
          Video connection services are temporarily unavailable. Please retry
          the call.
        </p>
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-2">
          <button
            type="button"
            onClick={handleRetryConnection}
            className="inline-flex items-center justify-center px-4 py-2.5 rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition text-sm font-semibold"
          >
            Retry connection
          </button>
          <button
            type="button"
            onClick={handleEndCall}
            className="inline-flex items-center justify-center px-4 py-2.5 rounded-lg bg-white border border-slate-300 text-slate-700 hover:bg-slate-100 transition text-sm font-medium"
          >
            Cancel
          </button>
        </div>
      </div>
    );
  }

  if (turnStatus === "loading") {
    return (
      <div
        className="w-full flex items-center justify-center text-center text-sm text-slate-600 bg-white border border-slate-200 rounded-xl p-6"
        data-turn-status={turnStatus}
        data-connection-status={connectionStatus}
      >
        Preparing secure video connection...
      </div>
    );
  }

  if (hasEnded) {
    return (
      <div className="w-full flex items-center justify-center text-center text-sm text-slate-600 bg-white border border-slate-200 rounded-xl p-6">
        Call ended.
      </div>
    );
  }

  const isScreenSharingPriority = localScreenSharing || remoteScreenSharing;
  const localIsPrimary = localScreenSharing;
  const remoteIsPrimary = remoteScreenSharing && !localScreenSharing;

  const normalizedRole = normalizeRole(currentUserRole);
  const remoteRoleFallback =
    normalizedRole === ROLE_VALUE.requester
      ? "Expert"
      : normalizedRole === ROLE_VALUE.specialist
      ? "Customer"
      : "Participant";
  const remoteDisplayName = remoteName || remoteRoleFallback;
  const localInitial = getInitial(
    currentUser?.displayName || currentUser?.email || localName
  );

  // Keep the two <video> elements in a fixed order (local, then remote) so
  // React never remounts them (which would drop their MediaStream srcObject).
  // Layout is driven purely by className changes.
  const panelClass = (isPrimary) => {
    if (!isScreenSharingPriority) {
      return `relative ${
        isFullscreen ? "h-full" : "h-56 sm:h-64"
      } rounded-lg overflow-hidden bg-slate-900`;
    }
    return isPrimary
      ? "absolute inset-0 rounded-lg overflow-hidden bg-slate-900"
      : "absolute bottom-3 right-3 w-32 h-24 sm:w-40 sm:h-28 rounded-lg overflow-hidden border-2 border-white/80 shadow-xl z-10 bg-slate-900";
  };
  const videoFit = (isPrimary) =>
    isScreenSharingPriority && isPrimary ? "object-contain" : "object-cover";

  const neutralBtn =
    "bg-slate-100 hover:bg-slate-200 text-slate-700 border border-slate-200";
  const dangerBtn = "bg-rose-500 hover:bg-rose-600 text-white";
  const activeShareBtn = "bg-blue-600 hover:bg-blue-700 text-white";

  return (
    <div
      ref={containerRef}
      className={`relative w-full bg-white border border-slate-200 overflow-hidden flex flex-col ${
        isFullscreen ? "h-screen rounded-none" : "rounded-xl"
      }`}
      data-turn-status={turnStatus}
      data-connection-status={connectionStatus}
      data-ice-server-count={iceDiagnostics.iceServerCount}
      title={
        turnReason
          ? undefined
          : `Connection: ${connectionState} · ICE: ${iceConnectionState}`
      }
    >
      {/* ISSUE 2 — blocking mirror-loop warning overlay */}
      {screenShareWarning && (
        <div className="absolute inset-0 z-30 flex items-center justify-center bg-slate-900/70 p-4">
          <div className="w-full max-w-sm rounded-2xl bg-white shadow-2xl border border-slate-200 p-6 text-center">
            <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-amber-50 border border-amber-100 flex items-center justify-center">
              <AlertTriangle className="w-6 h-6 text-amber-600" />
            </div>
            <h3 className="text-lg font-bold text-slate-900 mb-2">
              You are sharing this browser tab
            </h3>
            <p className="text-sm text-slate-600 mb-5 leading-relaxed">
              This will create a mirror loop. Please click Stop Sharing and share
              a different window instead — for example your code editor, terminal,
              or Stripe dashboard.
            </p>
            <button
              onClick={handleStopWarningShare}
              className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-rose-500 hover:bg-rose-600 text-white font-semibold text-sm shadow-sm transition"
            >
              <MonitorX className="w-4 h-4" />
              Stop Sharing
            </button>
            <button
              onClick={handleContinueScreenShare}
              className="mt-3 text-xs text-slate-500 hover:text-slate-700 underline"
            >
              Continue anyway
            </button>
          </div>
        </div>
      )}

      {/* Status bar */}
      <div className="flex items-center justify-between px-4 py-2.5 border-b border-slate-200">
        <div className="flex items-center gap-2">
          <span
            className={`w-2.5 h-2.5 rounded-full animate-pulse ${
              iceConnected ? "bg-emerald-500" : "bg-amber-400"
            }`}
          />
          <span className="text-sm font-medium text-slate-700">
            {iceConnected ? "Live session" : "Connecting..."}
          </span>
        </div>
        <div
          className="text-sm font-mono tabular-nums text-slate-500"
          title={`Connection: ${connectionState} · ICE: ${iceConnectionState}`}
        >
          {iceConnected ? formatDuration(elapsedSeconds) : "00:00"}
        </div>
      </div>

      {/* Video area — screen is the primary surface; cameras sit as tiles
          until someone shares, then collapse into a picture-in-picture. */}
      <div className={`p-3 ${isFullscreen ? "flex-1 min-h-0" : ""}`}>
        <div
          className={
            isScreenSharingPriority
              ? `relative w-full ${
                  isFullscreen ? "h-full" : "h-72 sm:h-96"
                } bg-slate-900 rounded-lg overflow-hidden`
              : `relative grid grid-cols-1 md:grid-cols-2 gap-3 ${
                  isFullscreen ? "h-full" : ""
                }`
          }
        >
          {!isScreenSharingPriority && iceConnected && (
            <div className="absolute inset-x-3 top-3 z-10 flex justify-center pointer-events-none">
              <p className="rounded-full bg-slate-900/60 px-3 py-1 text-2xs font-medium text-white">
                Share a window to make it the main view
              </p>
            </div>
          )}
          {/* Local panel */}
          <div className={panelClass(localIsPrimary)}>
            <video
              ref={localVideoRef}
              autoPlay
              muted
              playsInline
              className={`w-full h-full ${videoFit(localIsPrimary)} bg-slate-900`}
            />
            {!localScreenSharing && !videoEnabled && (
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-slate-800">
                <div className="w-16 h-16 rounded-full bg-slate-600 flex items-center justify-center text-white text-xl font-semibold">
                  {localInitial}
                </div>
                <span className="text-xs text-slate-300">Camera off</span>
              </div>
            )}
            <div className="absolute bottom-2 left-2 bg-slate-900/60 text-white text-xs px-2 py-0.5 rounded-md">
              {localName}
            </div>
          </div>

          {/* Remote panel */}
          <div className={panelClass(remoteIsPrimary)}>
            <video
              ref={remoteVideoRef}
              autoPlay
              playsInline
              className={`w-full h-full ${videoFit(remoteIsPrimary)} bg-slate-900`}
            />
            <div className="absolute bottom-2 left-2 bg-slate-900/60 text-white text-xs px-2 py-0.5 rounded-md">
              {remoteDisplayName}
            </div>
          </div>
        </div>
      </div>

      {/* Screen share banner */}
      {localScreenSharing && (
        <div className="mx-3 mb-1 flex items-center justify-between gap-3 rounded-lg bg-blue-50 border border-blue-200 px-3 py-2">
          <span className="flex items-center gap-2 text-sm font-medium text-blue-800">
            <MonitorUp className="w-4 h-4" />
            You are sharing your screen
          </span>
          <button
            onClick={stopScreenShare}
            className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full bg-white border border-blue-300 text-blue-700 hover:bg-blue-100 transition"
          >
            <MonitorX className="w-3.5 h-3.5" />
            Stop Sharing
          </button>
        </div>
      )}

      {/* Controls: share is the primary action because this is a debugging
          session, not a video meeting. Capture writes the current frame to
          the evidence library so findings can cite what was on screen. */}
      <div className="border-t border-slate-200 bg-white px-4 py-3">
        <div className="flex items-end justify-center gap-3 sm:gap-5">
          <div className="flex flex-col items-center gap-1">
            <button
              onClick={localScreenSharing ? stopScreenShare : startScreenShare}
              title={localScreenSharing ? "Stop sharing screen" : "Share screen"}
              aria-label={
                localScreenSharing ? "Stop sharing screen" : "Share screen"
              }
              className={`inline-flex items-center justify-center w-14 h-14 rounded-full transition shadow-md ${
                localScreenSharing ? activeShareBtn : "bg-blue-600 hover:bg-blue-700 text-white"
              }`}
            >
              {localScreenSharing ? (
                <MonitorX className="w-6 h-6" />
              ) : (
                <MonitorUp className="w-6 h-6" />
              )}
            </button>
            <span className="hidden sm:block text-xs font-medium text-slate-700">
              {localScreenSharing ? "Stop share" : "Share screen"}
            </span>
          </div>

          {onCaptureFrame && (
            <div className="flex flex-col items-center gap-1">
              <button
                type="button"
                onClick={handleCaptureFrame}
                disabled={capturing || !iceConnected}
                title="Save the current frame as evidence"
                aria-label="Capture frame as evidence"
                className={`inline-flex items-center justify-center w-12 h-12 rounded-full transition shadow-sm ${neutralBtn} disabled:cursor-not-allowed disabled:opacity-40`}
              >
                <Camera className="w-5 h-5" />
              </button>
              <span className="hidden sm:block text-xs text-slate-600">
                {capturing ? "Saving…" : "Capture"}
              </span>
            </div>
          )}

          <div className="flex flex-col items-center gap-1">
            <button
              onClick={toggleAudio}
              title={audioEnabled ? "Mute microphone" : "Unmute microphone"}
              aria-label={audioEnabled ? "Mute microphone" : "Unmute microphone"}
              className={`inline-flex items-center justify-center w-12 h-12 rounded-full transition shadow-sm ${
                audioEnabled ? neutralBtn : dangerBtn
              }`}
            >
              {audioEnabled ? (
                <Mic className="w-5 h-5" />
              ) : (
                <MicOff className="w-5 h-5" />
              )}
            </button>
            <span className="hidden sm:block text-xs text-slate-600">
              {audioEnabled ? "Mute" : "Unmute"}
            </span>
          </div>

          <div className="flex flex-col items-center gap-1">
            <button
              onClick={toggleVideo}
              title={videoEnabled ? "Turn camera off" : "Turn camera on"}
              aria-label={videoEnabled ? "Turn camera off" : "Turn camera on"}
              className={`inline-flex items-center justify-center w-12 h-12 rounded-full transition shadow-sm ${
                videoEnabled ? neutralBtn : dangerBtn
              }`}
            >
              {videoEnabled ? (
                <Video className="w-5 h-5" />
              ) : (
                <VideoOff className="w-5 h-5" />
              )}
            </button>
            <span className="hidden sm:block text-xs text-slate-600">
              {videoEnabled ? "Camera" : "Camera off"}
            </span>
          </div>

          <div className="flex flex-col items-center gap-1">
            <button
              onClick={toggleFullscreen}
              title={isFullscreen ? "Exit fullscreen" : "Expand to fullscreen"}
              aria-label={
                isFullscreen ? "Exit fullscreen" : "Expand to fullscreen"
              }
              className={`inline-flex items-center justify-center w-12 h-12 rounded-full transition shadow-sm ${
                isFullscreen ? activeShareBtn : neutralBtn
              }`}
            >
              {isFullscreen ? (
                <Minimize className="w-5 h-5" />
              ) : (
                <Maximize className="w-5 h-5" />
              )}
            </button>
            <span className="hidden sm:block text-xs text-slate-600">
              {isFullscreen ? "Exit" : "Expand"}
            </span>
          </div>

          <div className="flex flex-col items-center gap-1">
            <button
              onClick={handleEndCall}
              title="End call"
              aria-label="End call"
              className={`inline-flex items-center justify-center w-12 h-12 rounded-full transition shadow-md ${dangerBtn}`}
            >
              <PhoneOff className="w-5 h-5" />
            </button>
            <span className="hidden sm:block text-xs font-medium text-slate-700">
              End
            </span>
          </div>
        </div>
        <p className="mt-2 text-center text-xs text-slate-400">
          Share an application window, not this browser tab
        </p>
      </div>
    </div>
  );
}

export default WebRTCCall;

